import Phaser from "phaser";
import SoundManager from "../game/SoundManager";
import XAuth from "../game/XAuth";
import ScoreManager from "../game/ScoreManager";
import { createMascot } from "../game/WoodchuckMascot";
import MusicManager from "../game/MusicManager";
import { addMuteButton } from "../game/MuteButton";
import WalletManager from "../game/WalletManager";
import PaymentManager from "../game/PaymentManager";
import DemoConfig from "../game/DemoConfig";
import DailyChallengeManager from "../game/DailyChallengeManager";
import ChallengeManager from "../game/ChallengeManager";
import AchievementManager from "../game/AchievementManager";
import { FONT_SIZE, fontStyle } from "../game/FontScale";
import { fitText } from "../game/view/layout";
import { FREE_PLAY } from "../game/FreePlay";
import StageProgress from "../game/stage/StageProgress";
import RunClient from "../game/RunClient";
import { addStageSelectLink } from "../game/view/StageSelect";

// Minimum clear space between stacked rows (px)
const GAP = 8;

class MenuScene extends Phaser.Scene {
  constructor() {
    super({ key: "MenuScene" });
  }

  create() {
    this.cameras.main.setBackgroundColor("#0a0a0a");

    const W = this.scale.width;
    const H = this.scale.height;
    const CX = W / 2;
    const mob = W < 600;

    const xUser = XAuth.getUser();
    const xHandle = xUser ? xUser.handle : null;

    // Store for helper methods
    this._mob = mob;
    this._W = W;
    this._H = H;
    this._CX = CX;

    // ────────────────────────────────────────────────────────────
    //  MASCOT  (restored on mobile with portrait canvas)
    // ────────────────────────────────────────────────────────────
    const mascotY = mob ? 60 : 90;
    const mascotScale = mob ? 3 : 4;
    this.mascot = createMascot(this, CX, mascotY, mascotScale);

    // ────────────────────────────────────────────────────────────
    //  TITLE
    // ────────────────────────────────────────────────────────────
    const showDevBanner = !mob && DemoConfig.isDemoMode();
    const titleY = mob ? 150 : showDevBanner ? 178 : 180;
    this.add
      .text(
        CX,
        titleY,
        "WOODCHUCK",
        fontStyle(FONT_SIZE.TITLE, "#00FFFF", {
          stroke: "#007788",
          strokeThickness: mob ? 3 : 5,
        }),
      )
      .setOrigin(0.5);

    // ────────────────────────────────────────────────────────────
    //  IN DEVELOPMENT BANNER (demo mode only, desktop)
    // ────────────────────────────────────────────────────────────
    if (showDevBanner) {
      const devBanner = this.add
        .text(
          CX,
          titleY + 24,
          "IN DEVELOPMENT  -  DEMO",
          fontStyle(FONT_SIZE.CAPTION, "#FF0000"),
        )
        .setOrigin(0.5);
      fitText(devBanner, W - 16);
      this.tweens.add({
        targets: devBanner,
        alpha: { from: 1, to: 0.3 },
        duration: 1000,
        ease: "Sine.easeInOut",
        yoyo: true,
        repeat: -1,
      });
    }

    // ────────────────────────────────────────────────────────────
    //  GAMEPLAY HINT  (blinking, arcade-style)
    // ────────────────────────────────────────────────────────────
    const hintY = mob ? 180 : showDevBanner ? titleY + 42 : titleY + 32;
    const hint = this.add
      .text(
        CX,
        hintY,
        mob ? "TAP W + C!" : "TAP  W + C  TO CHUCK WOOD!",
        fontStyle(FONT_SIZE.BODY, "#FF00FF"),
      )
      .setOrigin(0.5);
    fitText(hint, W - 16);

    this.tweens.add({
      targets: hint,
      alpha: { from: 1, to: 0.25 },
      duration: 800,
      ease: "Sine.easeInOut",
      yoyo: true,
      repeat: -1,
    });

    // ────────────────────────────────────────────────────────────
    //  PLAY BUTTONS — requires X sign-in
    // ────────────────────────────────────────────────────────────
    // Everything below the play button is laid out with a y cursor so each
    // row gets its own band with at least GAP px of clear space.
    const bigH = mob ? 54 : 44; // matches bigBtn's rendered height
    const btnY = mob ? 225 : hintY + hint.height / 2 + GAP + bigH / 2;
    let y = btnY + bigH / 2 + GAP;

    if (xUser || FREE_PLAY) {
      // StageScene resumes the player's game on the right stage (the server's, or this device's in free play)
      const play = () => {
        if (this._stageSelectOpen) return;
        SoundManager.resume();
        MusicManager.play();
        this.scene.start("StageScene", { level: 1, newGame: FREE_PLAY });
      };
      const main = this.bigBtn(CX, btnY, 340, 44, FREE_PLAY ? "NEW GAME" : "PLAY", 0x00ffff, play);
      this.input.keyboard.on("keydown-SPACE", play);

      if (FREE_PLAY) {
        this.addTesterLink();
        const contH = mob ? 34 : 24;
        const game = StageProgress.get();
        if (!game.finished && (game.stage > 1 || game.lives < 3)) {
          this.showContinueBtn(y + contH / 2, game);
          y += contH + GAP;
        }
      } else {
        this.labelPlayButton(main.text, main.width);
      }
    } else {
      this.bigBtn(CX, btnY, 340, 44, "SIGN IN TO PLAY", 0xff00ff, () => {
        if (XAuth.isConfigured()) {
          XAuth.startAuth();
        }
      });

      const required = this.add
        .text(CX, y, "X ACCOUNT REQUIRED", fontStyle(FONT_SIZE.CAPTION, "#999999"))
        .setOrigin(0.5, 0);
      fitText(required, W - 16);
      y += required.height + GAP;

      if (!mob && DemoConfig.isDemoMode()) {
        const feedback = this.addFeedbackButton(CX, y).setOrigin(0.5, 0);
        y += feedback.height + GAP;
      }

      this.input.keyboard.on("keydown-SPACE", () => {
        if (XAuth.isConfigured()) {
          XAuth.startAuth();
        }
      });
    }

    // ────────────────────────────────────────────────────────────
    //  DAILY CHALLENGE BUTTON
    // ────────────────────────────────────────────────────────────
    if (DemoConfig.dailyChallengeEnabled()) {
      y = this._addDailyChallengeButton(y, mob) + GAP;
    }

    // ────────────────────────────────────────────────────────────
    //  NEON DIVIDER  (sits in the middle of a 2×GAP band)
    // ────────────────────────────────────────────────────────────
    const divGfx = this.add.graphics();
    divGfx.lineStyle(1, 0x00ffff, 0.3);
    divGfx.lineBetween(W * 0.125, y, W * 0.875, y);
    y += GAP;

    // ────────────────────────────────────────────────────────────
    //  HIGH SCORES TABLE  (always visible on landing page)
    // ────────────────────────────────────────────────────────────
    const scoreTitle = this.add
      .text(
        CX,
        y,
        "HIGH SCORES",
        fontStyle(FONT_SIZE.HEADING, "#FFD700", {
          stroke: "#8B6914",
          strokeThickness: 2,
        }),
      )
      .setOrigin(0.5, 0);
    y += scoreTitle.height + GAP;

    // Column positions scale with width: RANK and PLAYER left-aligned with
    // a clear gap between them, WOOD right-aligned.
    const cols = {
      rankX: W * (mob ? 0.04 : 0.14),
      playerX: W * (mob ? 0.18 : 0.23),
      scoreRight: mob ? W - 16 : W * 0.86,
    };
    this._cols = cols;

    const colY = y;
    const colHeader = this.add.text(
      cols.rankX,
      colY,
      "RANK",
      fontStyle(FONT_SIZE.CAPTION, "#999999"),
    );
    this.add.text(cols.playerX, colY, "PLAYER", fontStyle(FONT_SIZE.CAPTION, "#999999"));
    this.add
      .text(cols.scoreRight, colY, "WOOD", fontStyle(FONT_SIZE.CAPTION, "#999999"))
      .setOrigin(1, 0);

    const lineY = colY + colHeader.height + 4;
    const lineGfx = this.add.graphics();
    lineGfx.lineStyle(1, 0xffd700, 0.15);
    lineGfx.lineBetween(cols.rankX - 8, lineY, cols.scoreRight + 8, lineY);
    const rowsTop = lineY + 6;

    // ── Bottom block: links, pot box, footer (anchored to H) ───────
    const footY = H - (mob ? 30 : 24);
    const potBoxH = mob ? 36 : 28;

    // "VIEW ALL" and "HOW IT WORKS" links below score table
    const linkOffset = mob ? W * 0.22 : W * 0.1;
    const viewAll = this.add
      .text(CX - linkOffset, 0, "VIEW ALL", fontStyle(FONT_SIZE.CAPTION, "#00FFFF"))
      .setOrigin(0.5, 0)
      .setInteractive({ useHandCursor: true });
    viewAll.on("pointerdown", () => this.scene.start("HighScoreScene"));

    const howItWorks = this.add
      .text(CX + linkOffset, 0, "HOW IT WORKS", fontStyle(FONT_SIZE.CAPTION, "#FFD700"))
      .setOrigin(0.5, 0)
      .setInteractive({ useHandCursor: true });
    howItWorks.on("pointerdown", () => this.scene.start("DocsScene"));

    // As many score rows as fit between the table header and the bottom
    // block (footer text top → pot box → links, each GAP apart).
    const rowH = mob ? 30 : 18;
    const rowsLimit =
      footY - 6 - GAP - potBoxH - GAP - viewAll.height - GAP;
    const maxRows = Phaser.Math.Clamp(
      Math.floor((rowsLimit - rowsTop) / rowH),
      1,
      mob ? 5 : 8,
    );
    const rowsBottom = rowsTop + maxRows * rowH;

    const linksY = rowsBottom + GAP;
    viewAll.setY(linksY);
    howItWorks.setY(linksY);

    // Loading placeholder
    const loadingText = this.add
      .text(
        CX,
        (rowsTop + rowsBottom) / 2,
        "LOADING...",
        fontStyle(FONT_SIZE.BODY, "#777777"),
      )
      .setOrigin(0.5);

    ScoreManager.fetchLeaderboard(maxRows).then((scores) => {
      if (!this.scene.isActive()) return; // left the menu before it loaded
      loadingText.destroy();
      this.renderScoreRows(scores, xHandle, rowsTop, maxRows, rowH);
    });

    // ────────────────────────────────────────────────────────────
    //  EPOCH POT / DEMO STATUS
    // ────────────────────────────────────────────────────────────
    const potY = linksY + viewAll.height + GAP;
    const potBoxX = mob ? W * 0.06 : W * 0.2;
    const potBoxW = mob ? W * 0.88 : W * 0.6;
    const potTextW = potBoxW - 16;
    const potLabelY = mob ? potY + 12 : potY + 3;
    const potLine2Y = potY + 15;

    if (DemoConfig.isDemoMode()) {
      const devBg = this.add.graphics();
      devBg.fillStyle(0xff0000, 0.06);
      devBg.fillRoundedRect(potBoxX, potY, potBoxW, potBoxH, 4);
      devBg.lineStyle(1, 0xff0000, 0.3);
      devBg.strokeRoundedRect(potBoxX, potY, potBoxW, potBoxH, 4);

      const demoLabel = this.add
        .text(
          CX,
          potLabelY,
          mob ? "FREE DEMO" : "STAGE 1 FREE DEMO  -  MORE STAGES COMING SOON",
          fontStyle(FONT_SIZE.CAPTION, "#FF0000"),
        )
        .setOrigin(0.5, 0);
      fitText(demoLabel, potTextW);

      if (!mob) {
        const demoLine2 = this.add
          .text(
            CX,
            potLine2Y,
            "PAYMENTS, EPOCHS & 100 STAGES IN DEVELOPMENT",
            fontStyle(FONT_SIZE.CAPTION, "#999999"),
          )
          .setOrigin(0.5, 0);
        fitText(demoLine2, potTextW);
      }
    } else {
      const potBg = this.add.graphics();
      potBg.fillStyle(0xff00ff, 0.06);
      potBg.fillRoundedRect(potBoxX, potY, potBoxW, potBoxH, 4);
      potBg.lineStyle(1, 0xff00ff, 0.3);
      potBg.strokeRoundedRect(potBoxX, potY, potBoxW, potBoxH, 4);

      const potLabel = this.add
        .text(
          CX,
          potLabelY,
          "EPOCH POT: ...",
          fontStyle(FONT_SIZE.CAPTION, "#FF00FF"),
        )
        .setOrigin(0.5, 0);

      const potTimer = mob
        ? null
        : this.add
            .text(CX, potLine2Y, "", fontStyle(FONT_SIZE.CAPTION, "#999999"))
            .setOrigin(0.5, 0);

      const setPotLabel = (str) => fitText(potLabel.setText(str), potTextW);

      PaymentManager.getEpochInfo()
        .then((info) => {
          if (!this.scene.isActive()) return; // left the menu before it loaded
          if (info && info.epoch) {
            const usd = (info.epoch.prizePoolUsd || 0).toFixed(2);
            const split = info.tokenomics?.split?.prize || "";
            setPotLabel(
              mob
                ? `POT: $${usd}`
                : `EPOCH POT: $${usd}  |  ${split} TO WINNER`,
            );
            if (!mob && potTimer && info.epoch.daysLeft !== undefined) {
              const tier = info.tokenomics?.tier || "";
              const timerStr = `${info.epoch.daysLeft}D ${info.epoch.hoursLeft}H LEFT`;
              potTimer.setText(tier ? `${timerStr}  ·  ${tier}` : timerStr);
              fitText(potTimer, potTextW);
            }
          } else {
            setPotLabel("EPOCH POT: $0.00");
          }
        })
        .catch(() => {
          setPotLabel("EPOCH POT: $0.00");
        });
    }

    // ────────────────────────────────────────────────────────────
    //  FOOTER: AUTH + WALLET + INFO
    // ────────────────────────────────────────────────────────────
    if (xUser) {
      const dotX = mob ? 16 + 3 : W * 0.1375;
      const handleX = dotX + 8;
      this.add.graphics().fillStyle(0x39ff14).fillCircle(dotX, footY, 3);
      const handleText = this.add
        .text(handleX, footY, xHandle, fontStyle(FONT_SIZE.CAPTION, "#39FF14"))
        .setOrigin(0, 0.5);

      if (DemoConfig.isDemoMode()) {
        if (!mob) this.addFeedbackButton(CX, footY);
      } else if (!mob) {
        const walletKey = WalletManager.getPublicKey();
        if (walletKey) {
          this.add
            .text(
              CX,
              footY,
              `WALLET: ${WalletManager.getShortKey()}`,
              fontStyle(FONT_SIZE.CAPTION, "#9945FF"),
            )
            .setOrigin(0.5, 0.5);
        } else {
          const walletBtn = this.add
            .text(
              CX,
              footY,
              "CONNECT WALLET",
              fontStyle(FONT_SIZE.CAPTION, "#9945FF"),
            )
            .setOrigin(0.5, 0.5)
            .setInteractive({ useHandCursor: true });
          walletBtn.on("pointerdown", async () => {
            walletBtn.setText("CONNECTING...");
            try {
              await WalletManager.connect();
              if (xHandle) {
                const link = await WalletManager.linkToAccount(xHandle);
                if (!link.ok) {
                  walletBtn.setText("LINK FAILED - RETRY");
                  return;
                }
              }
              walletBtn.setText(`WALLET: ${WalletManager.getShortKey()}`);
              walletBtn.disableInteractive();
            } catch {
              walletBtn.setText("CONNECT WALLET");
            }
          });
        }
      }

      const signOut = this.add
        .text(
          // Mobile: stop short of the mute icon in the bottom-right corner
          mob ? W - 48 : W * 0.875,
          footY,
          "SIGN OUT",
          fontStyle(FONT_SIZE.CAPTION, "#777777"),
        )
        .setOrigin(1, 0.5)
        .setInteractive({ useHandCursor: true });
      signOut.on("pointerdown", () => {
        XAuth.signOut();
        WalletManager.disconnect();
        this.scene.restart();
      });

      // Handle may not run into the centre item (desktop) or SIGN OUT (mobile)
      const handleMaxW = mob
        ? signOut.x - signOut.width - GAP * 2 - handleX
        : CX - W * 0.125 - GAP - handleX;
      fitText(handleText, handleMaxW);

    }

    // ── Challenge URL detection ─────────────────────────────
    this._checkPendingChallenge();

    // ── Achievement progress ──────────────────────────────────
    if (!mob) {
      const achProgress = AchievementManager.getProgress();
      if (achProgress.unlocked > 0) {
        this.add
          .text(
            W - 100,
            30,
            `${achProgress.unlocked}/${achProgress.total}`,
            fontStyle(FONT_SIZE.CAPTION, "#FFD700"),
          )
          .setOrigin(1, 0.5);
        this.add.text(W - 95, 30, "\u2605", fontStyle(FONT_SIZE.CAPTION, "#FFD700"));
      }
    }

    // ── BACKGROUND MUSIC + MUTE BUTTON ───────────────────────
    MusicManager.play();
    this.input.once("pointerdown", () => MusicManager.play());
    addMuteButton(this);

  }

  // ── Show or update the CONTINUE button ────────────────────────
  /** PLAY shows what pressing it does: continue the game, today's free game, or a coin. */
  async labelPlayButton(text, width) {
    const res = await RunClient.getGame();
    if (!this.scene.isActive() || !res.ok) return;
    if (res.comp) this.addTesterLink();
    const hearts = (n) => "♥".repeat(Math.max(0, n));
    let label = "INSERT COIN  -  $1 = 3 LIVES";
    if (res.game && res.game.lives > 0) label = `CONTINUE STAGE ${res.game.stage}  ${hearts(res.game.lives)}`;
    else if (res.game) label = `CONTINUE? STAGE ${res.game.stage}`;
    else if (res.freeGameAvailable) label = "PLAY  -  FREE GAME TODAY";
    else if (res.credits > 0) label = `PLAY  -  ${res.credits} CREDIT${res.credits > 1 ? "S" : ""}`;
    fitText(text.setText(label), width - 16);
  }

  /** Tester accounts and free-play builds can start a game on any stage. */
  addTesterLink() {
    addStageSelectLink(this, {
      x: 12,
      y: this._H - 6,
      onPick: (stage) => {
        SoundManager.resume();
        MusicManager.play();
        this.scene.start("StageScene", { level: stage, pickStage: stage });
      },
    });
  }

  // `cy` is the centre of the row reserved for it in create(). Free play only.
  showContinueBtn(cy, game) {
    const CX = this._CX;
    if (this._continueBtn) {
      this._continueBtn.bg.destroy();
      this._continueBtn.text.destroy();
    }
    this._continueBtn = this.neonBtn(
      CX,
      cy,
      260,
      24,
      `CONTINUE STAGE ${game.stage}  ${"♥".repeat(game.lives)}`,
      0x39ff14,
      () => {
        SoundManager.resume();
        MusicManager.play();
        this.scene.start("StageScene", { level: game.stage });
      },
    );
  }

  // ── Feedback button (opens X compose with @woodchuckgame) ─────
  addFeedbackButton(cx, cy) {
    const feedbackBtn = this.add
      .text(
        cx,
        cy,
        "FEEDBACK  @woodchuckgame",
        fontStyle(FONT_SIZE.CAPTION, "#00FFFF"),
      )
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true });
    fitText(feedbackBtn, this._W - 16);

    feedbackBtn.on("pointerdown", () => {
      XAuth.openShareIntent("Hey @woodchuckgame - feedback on the demo:\n\n");
    });
    return feedbackBtn;
  }

  // ── Render score rows on landing page ───────────────────────────
  // Rows fill the band [startY, startY + maxRows * rowH) computed in create().
  renderScoreRows(scores, myHandle, startY, maxRows, rowH) {
    const W = this._W;
    const { rankX, playerX, scoreRight } = this._cols;

    if (!scores || scores.length === 0) {
      const midY = startY + (maxRows * rowH) / 2;
      this.add
        .text(
          this._CX,
          midY - 10,
          "NO SCORES YET",
          fontStyle(FONT_SIZE.SUBHEADING, "#777777"),
        )
        .setOrigin(0.5);
      const cta = this.add
        .text(
          this._CX,
          midY + 10,
          "BE THE FIRST TO PLAY!",
          fontStyle(FONT_SIZE.BODY, "#999999"),
        )
        .setOrigin(0.5);
      fitText(cta, W - 16);
      return;
    }

    scores.slice(0, maxRows).forEach((entry, i) => {
      const y = startY + i * rowH;
      const rank = i + 1;
      const isMe = myHandle && entry.handle === myHandle;
      const handle = (entry.handle || "ANON").substring(0, 14);

      if (isMe) {
        const hl = this.add.graphics();
        hl.fillStyle(0x39ff14, 0.08);
        hl.fillRect(rankX - 8, y - 2, scoreRight - rankX + 16, rowH);
      }

      let rankColor = "#999999";
      let rankText = `${rank}.`;
      if (rank === 1) {
        rankColor = "#FFD700";
        rankText = "1ST";
      } else if (rank === 2) {
        rankColor = "#C0C0C0";
        rankText = "2ND";
      } else if (rank === 3) {
        rankColor = "#CD7F32";
        rankText = "3RD";
      }

      this.add.text(rankX, y, rankText, fontStyle(FONT_SIZE.BODY, rankColor));

      const scoreEl = this.add
        .text(
          scoreRight,
          y,
          `${entry.score}`,
          fontStyle(FONT_SIZE.BODY, isMe ? "#FFD700" : "#F5E6C8"),
        )
        .setOrigin(1, 0);

      const handleEl = this.add.text(
        playerX,
        y,
        handle,
        fontStyle(FONT_SIZE.BODY, isMe ? "#39FF14" : "#D4884A"),
      );
      // Keep the handle clear of the score column
      fitText(handleEl, scoreRight - scoreEl.width - GAP * 2 - playerX);
    });
  }

  // ── Daily Challenge button + streak ──────────────────────────────
  // `top` is the top of the band for this block; returns its bottom y.
  _addDailyChallengeButton(top, mob) {
    const CX = this._CX;
    const W = this._W;
    const alreadyPlayed = DailyChallengeManager.hasPlayedToday();
    const todayResult = DailyChallengeManager.getTodayResult();
    const streak = DailyChallengeManager.getStreak();

    let label = mob ? "DAILY" : "DAILY CHALLENGE";
    let statusText = "FREE PLAY";
    if (alreadyPlayed && todayResult) {
      label = mob ? "DAILY" : "DAILY CHALLENGE";
      statusText = `WOOD: ${todayResult.score.toLocaleString()}`;
      if (todayResult.rank) {
        statusText += ` | #${todayResult.rank}`;
      }
    }

    // Desktop: button left of centre, streak to the right of it
    const btnCX = mob ? CX : CX - W * 0.1125;
    const btnH = mob ? 34 : 24;
    const y = top + btnH / 2;
    this.neonBtn(btnCX, y, 200, btnH, label, 0xffd700, () => {
      this.scene.start("DailyChallengeScene");
    });

    const status = this.add
      .text(
        btnCX,
        top + btnH + GAP,
        statusText,
        fontStyle(FONT_SIZE.CAPTION, alreadyPlayed ? "#7BC74A" : "#C4935A"),
      )
      .setOrigin(0.5, 0);
    fitText(status, mob ? W - 16 : W * 0.3);

    if (!mob && streak && streak.currentStreak >= 3) {
      const streakX = CX + W * 0.1125;
      this.add
        .text(
          streakX,
          y - 9,
          `${streak.currentStreak}`,
          fontStyle(FONT_SIZE.BODY, "#FF6600"),
        )
        .setOrigin(0.5);

      this.add
        .text(streakX, y + 4, "STREAK", fontStyle(FONT_SIZE.CAPTION, "#C4935A"))
        .setOrigin(0.5);

    }

    return status.y + status.height;
  }

  // ── Challenge URL detection ────────────────────────────────────
  async _checkPendingChallenge() {
    const challengeId = ChallengeManager.getPendingChallengeId();
    if (!challengeId) return;

    const CX = this._CX;
    const W = this._W;

    try {
      const challenge = await ChallengeManager.fetchChallenge(challengeId);
      if (challenge.status === "completed") return;

      const bannerBg = this.add.graphics().setDepth(500);
      bannerBg.fillStyle(0xff00ff, 0.9);
      bannerBg.fillRoundedRect(W * 0.125, 250, W * 0.75, 80, 8);

      const challengeFrom = this.add
        .text(
          CX,
          270,
          `CHALLENGE FROM ${challenge.creatorHandle}`,
          fontStyle(FONT_SIZE.BODY, "#FFFFFF"),
        )
        .setOrigin(0.5)
        .setDepth(501);
      fitText(challengeFrom, W * 0.75 - 16);

      this.add
        .text(
          CX,
          290,
          `THEIR WOOD: ${(challenge.creatorScore || 0).toLocaleString()}`,
          fontStyle(FONT_SIZE.CAPTION, "#FFD700"),
        )
        .setOrigin(0.5)
        .setDepth(501);

      const acceptBtn = this.add
        .text(
          CX,
          315,
          "ACCEPT CHALLENGE",
          fontStyle(FONT_SIZE.BODY, "#0a0a0a", {
            backgroundColor: "#FFD700",
            padding: { x: 12, y: 6 },
          }),
        )
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true })
        .setDepth(501);

      acceptBtn.on("pointerdown", () => {
        const url = new URL(window.location);
        url.searchParams.delete("challenge");
        window.history.replaceState({}, "", url);

        this.scene.start("StageScene", { level: 1, seed: challenge.seed, challenge: true });
      });
    } catch {
      // Challenge not found or error — ignore
    }
  }

  // ── Big prominent button (for START) ─────────────────────────────
  bigBtn(cx, cy, w, h, label, color, onClick) {
    const mob = this._mob;
    const bw = mob ? Math.min(w, this._W * 0.95) : w;
    const bh = mob ? Math.max(h, 54) : h;
    const x = cx - bw / 2;
    const y = cy - bh / 2;

    const bg = this.add.graphics();
    bg.fillStyle(color, 0.15);
    bg.fillRoundedRect(x, y, bw, bh, 6);
    bg.lineStyle(2, color, 1);
    bg.strokeRoundedRect(x, y, bw, bh, 6);
    bg.setInteractive(
      new Phaser.Geom.Rectangle(x, y, bw, bh),
      Phaser.Geom.Rectangle.Contains,
    );
    bg.on("pointerdown", onClick);

    const text = this.add
      .text(
        cx,
        cy,
        label,
        fontStyle(FONT_SIZE.HEADING, `#${color.toString(16).padStart(6, "0")}`),
      )
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    fitText(text, bw - 16);
    text.on("pointerdown", onClick);

    this.tweens.add({
      targets: bg,
      alpha: { from: 1, to: 0.5 },
      duration: 1000,
      ease: "Sine.easeInOut",
      yoyo: true,
      repeat: -1,
    });

    return { bg, text, width: bw };
  }

  // ── Small neon button ───────────────────────────────────────────
  neonBtn(cx, cy, w, h, label, color, onClick) {
    const mob = this._mob;
    const bh = mob ? Math.max(h, 34) : h;
    const x = cx - w / 2;
    const y = cy - bh / 2;

    const bg = this.add.graphics();
    bg.fillStyle(color, 0.06);
    bg.fillRoundedRect(x, y, w, bh, 4);
    bg.lineStyle(1, color, 0.7);
    bg.strokeRoundedRect(x, y, w, bh, 4);
    bg.setInteractive(
      new Phaser.Geom.Rectangle(x, y, w, bh),
      Phaser.Geom.Rectangle.Contains,
    );
    bg.on("pointerdown", onClick);

    const text = this.add
      .text(
        cx,
        cy,
        label,
        fontStyle(FONT_SIZE.BODY, `#${color.toString(16).padStart(6, "0")}`),
      )
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    fitText(text, w - 12);
    text.on("pointerdown", onClick);

    return { bg, text };
  }
}

export default MenuScene;
